import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch } from '../../store/store';

// Utils
import generateIdDevice from '../../utils/generateIdDevice';
import { extractChannelNumberFromId } from '../../utils/channel-utils';

// Enums
import { ChangeChannelAction } from '../../enums/action';

// Components
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import CheckboxCircleControl from '../CheckboxCircleControl/CheckboxCircleControl';
import { toast } from 'react-toastify';

// Types
import { ButtonType } from '../../types/button-type';
import { ElementSize } from '../../types/element-size';
import { CycleController } from '../../store/models/controllers';
import { ChannelActionItem } from '../../store/models/channels';

type ComponentProps = {
  isVisible: boolean;
  onClose: () => void;
  cycleId: string;
  cycleControllers: CycleController[];
  deviceNames: { [key: string]: string };
};

type DispatchProps = ReturnType<typeof mapDispatch>;

type ChannelQuickSetProps = ComponentProps & DispatchProps;

type ValueType = 'power' | 'trim' | 'setpoint';

const ChannelQuickSet: React.FC<ChannelQuickSetProps> = ({
  isVisible,
  onClose,
  cycleId,
  cycleControllers,
  deviceNames,
  updateChannels,
}) => {
  const [powerValue, setPowerValue] = useState<string>('');
  const [trimValue, setTrimValue] = useState<string>('');
  const [setpointValue, setSetpointValue] = useState<string>('');
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [applyingType, setApplyingType] = useState<ValueType | null>(null);

  // Validation limits (aligned with channelActions limits / backend
  // CHANNEL_PREFERENCES_LIMITS.SETPOINT_ADJUST)
  const POWER_MIN = 0;
  const POWER_MAX = 100;
  const TRIM_MIN = -1500;
  const TRIM_MAX = 1500;
  const SETPOINT_MIN = 0.1;
  const SETPOINT_MAX = 2400;

  // Get all available channels from cycle controllers
  const availableChannels = useMemo(() => {
    const channels: Array<{ id: string; name: string; controllerId: string; channelNum: number | null; channelRaw: string }> = [];
    
    cycleControllers.forEach((cycleController) => {
      const serialNumber = cycleController.controller.serial_number;
      
      cycleController.channels.forEach((channel) => {
        const channelRaw = channel !== undefined && channel !== null ? String(channel) : '';
        if (!channelRaw) return; // skip invalid entries
        
        // Generate proper device ID (e.g., "000T29YM#C-5")
        const idDevice = generateIdDevice(serialNumber, channelRaw);
        const deviceName = deviceNames[idDevice] || `Channel ${channelRaw}`;
        
        // Extract numeric channel number using the existing helper
        const channelNum = extractChannelNumberFromId(idDevice);
        if (channelNum === null) return; // skip channels we can't extract number from

        channels.push({
          id: idDevice, // use full device ID for selection
          name: deviceName,
          controllerId: serialNumber,
          channelNum,
          channelRaw,
        });
      });
    });
    
    return channels;
  }, [cycleControllers, deviceNames]);

  const toggleChannel = (channelId: string) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channelId)) {
      newSelected.delete(channelId);
    } else {
      newSelected.add(channelId);
    }
    setSelectedChannels(newSelected);
  };

  const selectAll = () => {
    const allChannelIds = new Set(availableChannels.map((ch) => ch.id));
    setSelectedChannels(allChannelIds);
  };

  const selectNone = () => {
    setSelectedChannels(new Set());
  };

  const VALUE_TYPE_CONFIG: Record<
    ValueType,
    { label: string; value: string; min: number; max: number; action: ChangeChannelAction }
  > = {
    power: {
      label: 'power',
      value: powerValue,
      min: POWER_MIN,
      max: POWER_MAX,
      action: ChangeChannelAction.SET_CHANNEL_POWER_SCALE,
    },
    trim: {
      label: 'trim',
      value: trimValue,
      min: TRIM_MIN,
      max: TRIM_MAX,
      action: ChangeChannelAction.SET_CHANNEL_TRIM_CHANNEL_SETPOINT,
    },
    setpoint: {
      label: 'set point',
      value: setpointValue,
      min: SETPOINT_MIN,
      max: SETPOINT_MAX,
      // Sent to the device with the SPAdjust command - see
      // adjustChannelSetpoint on the backend.
      action: ChangeChannelAction.SET_CHANNEL_SETPOINT_ADJUST,
    },
  };

  // The set point is the one Quick Set value that always goes to every
  // channel in the cycle - it doesn't use the channel selection above.
  const applyValues = async (type: ValueType) => {
    const appliesToAllChannels = type === 'setpoint';

    if (!appliesToAllChannels && selectedChannels.size === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    const { label, value, min, max, action } = VALUE_TYPE_CONFIG[type];
    if (value === '' || value === null || value === undefined) {
      toast.error(`Please enter a value for ${label}`);
      return;
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      toast.error('Value must be a number');
      return;
    }

    if (numeric < min || numeric > max) {
      toast.error(`${label[0].toUpperCase()}${label.slice(1)} must be between ${min} and ${max}`);
      return;
    }

    setApplyingType(type);

    try {
      const channelList: ChannelActionItem[] = availableChannels
        .filter((ch) => appliesToAllChannels || selectedChannels.has(ch.id))
        .filter((ch) => ch.channelNum !== null) // ensure valid channel number
        .map((ch) => ({
          controller_serial_number: ch.controllerId,
          channel_num: String(ch.channelNum), // use extracted numeric channel
          value: value,
        }));

      if (channelList.length === 0) {
        toast.error(appliesToAllChannels ? 'No channels found in this cycle' : 'No valid channels selected');
        return;
      }

      await updateChannels({
        cycleId,
        data: {
          action,
          list: channelList,
        },
      });

      // Reset after successful application
      if (!appliesToAllChannels) {
        setSelectedChannels(new Set());
      }
      onClose();
    } catch (error) {
      console.error('Error applying channel values:', error);
    } finally {
      setApplyingType(null);
    }
  };

  const isPowerValid = () => {
    if (!powerValue) return false;
    const v = Number(powerValue);
    return !Number.isNaN(v) && v >= POWER_MIN && v <= POWER_MAX;
  };

  const isTrimValid = () => {
    if (!trimValue) return false;
    const v = Number(trimValue);
    return !Number.isNaN(v) && v >= TRIM_MIN && v <= TRIM_MAX;
  };

  const isSetpointValid = () => {
    if (!setpointValue) return false;
    const v = Number(setpointValue);
    return !Number.isNaN(v) && v >= SETPOINT_MIN && v <= SETPOINT_MAX;
  };

  const handleClose = () => {
    setSelectedChannels(new Set());
    setPowerValue('');
    setTrimValue('');
    setSetpointValue('');
    onClose();
  };

  return (
    <Modal
      modalTitle="Channel Quick Set"
      isVisible={isVisible}
      onClose={handleClose}
      className="min-h-[500px] w-[600px]"
      showCancelBtn={false}
      showAcceptBtn={false}
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Power and Trim Input Fields */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium text-slate-gray dark:text-white">
              P-Lim
            </label>
            <input
              type="number"
              value={powerValue}
              placeholder="Type a value"
              onChange={(e) => setPowerValue(e.target.value)}
              className="w-full rounded border border-foggy-mist bg-white px-3 py-2 text-sm text-black outline-none focus:border-red dark:border-strokedark dark:bg-boxdark dark:text-white"
              min="0"
              max="100"
            />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium text-slate-gray dark:text-white">
              Trim
            </label>
            <input
              type="number"
              value={trimValue}
              placeholder="Type a value"
              onChange={(e) => setTrimValue(e.target.value)}
              className="w-full rounded border border-foggy-mist bg-white px-3 py-2 text-sm text-black outline-none focus:border-red dark:border-strokedark dark:bg-boxdark dark:text-white"
              min="-1500"
              max="1500"
            />
          </div>
        </div>

        {/* Channel Selection */}
        <div className="flex flex-col gap-2 rounded-lg border-2 border-red p-4">
          <div className="mb-2 text-sm font-medium text-slate-gray dark:text-white">
            Select Channels
          </div>
          
          <div className="mb-3 flex flex-col gap-2 max-h-56 overflow-auto">
            {availableChannels.map((channel) => (
              <div key={channel.id} className="flex items-center gap-2">
                <CheckboxCircleControl
                  id={channel.id}
                  name={channel.id}
                  checked={selectedChannels.has(channel.id)}
                  onChange={() => toggleChannel(channel.id)}
                  label={channel.name}
                />
              </div>
            ))}
          </div>

          {/* Select All / None Buttons */}
          <div className="flex gap-2">
            <InteractiveButton
              size={ElementSize.SM}
              type={ButtonType.Secondary}
              text="None"
              onClick={selectNone}
              className="flex-1"
            />
            <InteractiveButton
              size={ElementSize.SM}
              type={ButtonType.Primary}
              text="All"
              onClick={selectAll}
              className="flex-1"
            />
          </div>
        </div>

        {/* Apply Buttons */}
        <div className="flex gap-2">
          <InteractiveButton
            size={ElementSize.MD}
            type={ButtonType.Primary}
            text={applyingType === 'power' ? 'Applying...' : 'Write Powers'}
            onClick={() => applyValues('power')}
            disabled={!isPowerValid() || selectedChannels.size === 0 || applyingType !== null}
            className="flex-1"
          />
          <InteractiveButton
            size={ElementSize.MD}
            type={ButtonType.Primary}
            text={applyingType === 'trim' ? 'Applying...' : 'Write Trims'}
            onClick={() => applyValues('trim')}
            disabled={!isTrimValid() || selectedChannels.size === 0 || applyingType !== null}
            className="flex-1"
          />
        </div>

        {/* Send an updated set point via the SPAdjust command - always
            applies to every channel in the cycle, regardless of the
            selection above. */}
        <div className="flex flex-col gap-2 border-t border-foggy-mist pt-4 dark:border-strokedark">
          <label className="text-sm font-medium text-slate-gray dark:text-white">
            New Set Point (all channels in this cycle)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={setpointValue}
              placeholder="Type a value"
              onChange={(e) => setSetpointValue(e.target.value)}
              className="w-full rounded border border-foggy-mist bg-white px-3 py-2 text-sm text-black outline-none focus:border-red dark:border-strokedark dark:bg-boxdark dark:text-white"
              min={SETPOINT_MIN}
              max={SETPOINT_MAX}
              step="any"
            />
            <InteractiveButton
              size={ElementSize.MD}
              type={ButtonType.Primary}
              text={applyingType === 'setpoint' ? 'Applying...' : 'Send Set Point'}
              onClick={() => applyValues('setpoint')}
              disabled={!isSetpointValid() || applyingType !== null}
              className="flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  updateChannels: dispatch.channels.updateChannels,
});

export default connect<Record<string, never>, DispatchProps, ComponentProps>(null, mapDispatch)(ChannelQuickSet);
